/**
 * safeStorage — wrappers around localStorage and sessionStorage that don't
 * throw in private/incognito mode.
 *
 * Why: Brave private mode, Safari iOS private mode, Firefox with strict
 * cookie policies, and similar environments all throw `SecurityError` when
 * code touches the storage APIs. A single unguarded `localStorage.getItem`
 * at module-init or render time will crash the whole React tree before
 * mount. We hit this 3+ times in production (rounds 1-3 of post-deploy
 * fixes) — every new file that touches storage is one missed try/catch
 * away from another regression.
 *
 * Usage:
 *   import { safeLocal, safeSession } from '../utils/safeStorage';
 *   const value = safeLocal.get('preferred_metro');    // string | null
 *   safeLocal.set('preferred_metro', 'Austin');        // void; silent on failure
 *   safeLocal.remove('preferred_metro');               // void; silent on failure
 *
 * Note (audit #7): Do NOT use this wrapper to store the auth JWT. The token
 * lives in the HttpOnly session_token cookie and in-memory Zustand state
 * only. Persisting it to localStorage defeats the HttpOnly protection
 * against XSS.
 *
 * Lint enforcement: an ESLint rule (`no-restricted-globals` for
 * `localStorage`/`sessionStorage`) prevents future code from bypassing
 * these wrappers.
 *
 * Note: Zustand's `persist` middleware uses storage internally via its
 * own adapter and handles failures itself; this wrapper is for direct
 * application-level reads and writes.
 */

interface SafeStorage {
  /** Returns the stored value or `null` if the key is missing or storage is unavailable. */
  get(key: string): string | null;
  /** Stores a string. Silent no-op when storage is unavailable. */
  set(key: string, value: string): void;
  /** Removes a key. Silent no-op when storage is unavailable. */
  remove(key: string): void;
  /** Clears the storage. Silent no-op when storage is unavailable. */
  clear(): void;
}

// Resolve the underlying Storage lazily. Even *accessing* `window.localStorage`
// can throw in some browser configurations (e.g., Safari with cookies fully
// disabled), so we defer the lookup to call time and wrap it.
function makeSafe(getStorage: () => Storage | undefined): SafeStorage {
  return {
    get(key) {
      try {
        const s = getStorage();
        return s ? s.getItem(key) : null;
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        getStorage()?.setItem(key, value);
      } catch {
        /* private mode / quota exceeded / disabled */
      }
    },
    remove(key) {
      try {
        getStorage()?.removeItem(key);
      } catch {
        /* private mode / disabled */
      }
    },
    clear() {
      try {
        getStorage()?.clear();
      } catch {
        /* private mode / disabled */
      }
    },
  };
}

export const safeLocal: SafeStorage = makeSafe(() =>
  typeof window !== 'undefined' ? window.localStorage : undefined
);

export const safeSession: SafeStorage = makeSafe(() =>
  typeof window !== 'undefined' ? window.sessionStorage : undefined
);

/**
 * Storage-shaped adapters for Zustand's `createJSONStorage`.
 *
 * Zustand's persist middleware accepts a `Storage` (the DOM interface with
 * `getItem`/`setItem`/`removeItem`/`length`/`key`/`clear`). Passing the bare
 * `() => sessionStorage` callback throws SecurityError at adapter creation
 * time in private mode. These adapters satisfy the full Storage interface
 * by routing all reads/writes through safeLocal/safeSession; calls become
 * no-ops in private mode instead of crashing the store.
 *
 * Usage:
 *   import { persist, createJSONStorage } from 'zustand/middleware';
 *   import { safeSessionStorage } from '@/utils/safeStorage';
 *
 *   persist(state, {
 *     name: 'taco-foo',
 *     storage: createJSONStorage(() => safeSessionStorage),
 *   });
 */
function makeStorageAdapter(
  safe: SafeStorage,
  resolveNative: () => Storage | undefined
): Storage {
  return {
    get length(): number {
      try {
        return resolveNative()?.length ?? 0;
      } catch {
        return 0;
      }
    },
    clear: () => safe.clear(),
    getItem: (key: string) => safe.get(key),
    setItem: (key: string, value: string) => safe.set(key, value),
    removeItem: (key: string) => safe.remove(key),
    key: (index: number): string | null => {
      try {
        return resolveNative()?.key(index) ?? null;
      } catch {
        return null;
      }
    },
  };
}

export const safeSessionStorage: Storage = makeStorageAdapter(safeSession, () =>
  typeof window !== 'undefined' ? window.sessionStorage : undefined
);
